import dayjs from 'dayjs';
import type { Schedule, ScheduleParticipant } from '../types';

/** 子任务行（与任务表单 processedSubTasks 对齐） */
export interface ProjectSubTaskScheduleInput {
  userId: string;
  userName: string;
  roleType: string;
  roleTypeName?: string;
  startDate: string;
  endDate: string;
}

export interface BuildSchedulesForProjectTaskParams {
  taskId: string;
  title: string;
  description?: string;
  taskTypeLabel?: string;
  location?: string;
  project: {
    id: string;
    name: string;
    companyId?: string;
    companyName?: string;
  };
  /**
   * 仅当有任务人（可执行人）时生成日程：每人一条。
   * 无任务人则返回空数组——日程必须有人执行。
   */
  subTasks: ProjectSubTaskScheduleInput[];
  /**
   * 当子任务行未通过表单校验为空，但任务上仍有任务人（如仅选了人与日期未选角色）时，
   * 用任务级起止日期为每位任务人生成日程。
   */
  fallbackTaskUsers?: { id: string; name: string }[];
  taskStartDate?: unknown;
  taskEndDate?: unknown;
}

/** 表单 DatePicker / 字符串统一为 YYYY-MM-DD */
export function toScheduleDateYmd(v: unknown): string | undefined {
  if (v == null || v === '') return undefined;
  if (typeof v === 'string') return v;
  const x = dayjs(v as Parameters<typeof dayjs>[0]);
  return x.isValid() ? x.format('YYYY-MM-DD') : undefined;
}

const stamp = () => dayjs().format('YYYY-MM-DD HH:mm:ss');

/**
 * 项目任务 → 项目类日程：仅当存在任务人时，为每位任务人生成一条日程。
 * 无任务人不生成日程（不再生成「仅任务无执行人」的占位日程）。
 */
export function buildSchedulesForProjectTask(p: BuildSchedulesForProjectTaskParams): Schedule[] {
  let rows = p.subTasks.filter((st) => String(st.userId || '').trim() !== '');
  if (rows.length === 0 && p.fallbackTaskUsers && p.fallbackTaskUsers.length > 0) {
    const sd = toScheduleDateYmd(p.taskStartDate);
    const ed = toScheduleDateYmd(p.taskEndDate) || sd;
    if (sd) {
      const start = sd;
      const end = ed || sd;
      rows = p.fallbackTaskUsers.map((u) => ({
        userId: u.id,
        userName: u.name,
        roleType: 'assignee',
        roleTypeName: '任务人',
        startDate: start,
        endDate: end,
      }));
    }
  }
  if (rows.length === 0) {
    return [];
  }

  const seenAssignees = new Set<string>();
  rows = rows.filter((st) => {
    const dedupeKey = [
      String(st.userId || '').trim(),
      String(st.roleType || '').trim(),
      toScheduleDateYmd(st.startDate) || String(st.startDate || '').trim(),
      toScheduleDateYmd(st.endDate) || String(st.endDate || '').trim(),
    ].join('|');
    if (!dedupeKey || seenAssignees.has(dedupeKey)) {
      return false;
    }
    seenAssignees.add(dedupeKey);
    return true;
  });

  const ts = stamp();

  return rows.map((st, index) => {
    const roleLabel = st.roleTypeName || st.roleType;
    const sd = toScheduleDateYmd(st.startDate) || String(st.startDate);
    const ed = toScheduleDateYmd(st.endDate) || String(st.endDate);
    return {
      id: `${p.taskId}_schedule_${index}`,
      title: `${st.userName}-${roleLabel}-${p.title}`,
      description: [
        `项目：${p.project.name}`,
        p.taskTypeLabel ? `任务类型：${p.taskTypeLabel}` : '',
        roleLabel ? `角色：${roleLabel}` : '',
      ]
        .filter(Boolean)
        .join('\n'),
      category: 'project' as const,
      startTime: `${sd} 09:00:00`,
      endTime: `${ed} 18:00:00`,
      location: p.location || '',
      projectId: p.project.id,
      projectName: p.project.name,
      customerId: p.project.companyId,
      customerName: p.project.companyName,
      linkedTaskId: p.taskId,
      participants: [{ id: st.userId, name: st.userName || st.userId }] as ScheduleParticipant[],
      owner: st.userName || st.userId,
      status: 'scheduled',
      createdAt: ts,
      updatedAt: ts,
    };
  });
}
